import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmActionDialog } from '@/components/ui/confirm-action-dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { formatDeleteError } from '@/lib/api';

export default function WorkerCategoriesListPage() {
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [form, setForm] = useState({ name: '', skillLevel: 'UNSKILLED' });
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ item: any; action: 'activate' | 'deactivate' } | null>(null);
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canDelete = hasPermission('worker_categories:delete');

  useEffect(() => { loadCategories(); }, [statusFilter]);

  async function loadCategories() {
    setLoading(true);
    setLoadError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get<{ data: any[] }>(`/labour-categories${qs}`);
      setCategories(res.data || []);
    } catch (e: any) {
      setLoadError(e instanceof ApiError ? e.message : 'Unable to load Worker Categories');
    }
    setLoading(false);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = { name: form.name.trim(), skillLevel: form.skillLevel };
      if (editing) {
        await api.put(`/labour-categories/${editing.id}`, payload);
      } else {
        await api.post('/labour-categories', payload);
      }
      const wasEditing = !!editing;
      setForm({ name: '', skillLevel: 'UNSKILLED' });
      setEditing(null);
      addToast(wasEditing ? 'Updated' : 'Created', 'success');
      await loadCategories();
    } catch (e: any) {
      if (e instanceof ApiError && e.fields) {
        const msgs = Object.values(e.fields);
        setError(msgs.length > 0 ? msgs.join('; ') : e.message);
      } else {
        setError(e?.message || 'Failed');
      }
    }
    setSaving(false);
  }

  async function handleToggleStatus(item: any) {
    setToggling(item.id);
    try {
      await api.put(`/labour-categories/${item.id}`, { isActive: !item.is_active });
      addToast(item.is_active ? `${item.name} deactivated successfully.` : `${item.name} activated successfully.`, 'success');
      await loadCategories();
    } catch (e: any) { addToast(e?.message || 'Failed', 'error'); }
    setToggling(null);
  }

  const handleDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/labour-categories/${deleteTarget.id}`);
      addToast('Worker category permanently deleted.', 'success');
      setDeleteTarget(null);
      await loadCategories();
    } catch (e: unknown) {
      addToast(formatDeleteError(e, 'worker category'), 'error');
    }
    setDeleting(false);
  }, [deleteTarget, deleting]);

  const skillLevels = ['SKILLED', 'SEMI_SKILLED', 'UNSKILLED'];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">Worker Categories</h1>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 space-y-1"><label className="text-sm font-medium">Trade</label><Input placeholder="e.g. Mason" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="w-44 space-y-1"><label className="text-sm font-medium">Skill Level</label>
              <Select value={form.skillLevel} onValueChange={v => setForm(f => ({ ...f, skillLevel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{skillLevels.map(t => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()}>{editing ? 'Update' : <><Plus size={16} className="mr-1" />Add</>}</Button>
            {editing && <Button variant="ghost" onClick={() => { setEditing(null); setForm({ name: '', skillLevel: 'UNSKILLED' }); }}>Cancel</Button>}
          </div>
          {error && <p className="text-sm text-error mt-2">{error}</p>}
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            {(['all', 'active', 'inactive'] as const).map(v => (
              <Button key={v} type="button" size="sm" variant={statusFilter === v ? 'default' : 'outline'} onClick={() => setStatusFilter(v)}>
                {v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive'}
              </Button>
            ))}
          </div>
          {loading ? (
            <p className="text-center py-8 text-secondary">Loading...</p>
          ) : loadError ? (
            <div className="text-center py-8">
              <p className="text-sm text-error mb-3">{loadError}</p>
              <Button variant="outline" size="sm" onClick={loadCategories}>Retry</Button>
            </div>
          ) : categories.length === 0 ? (
            <p className="text-center py-8 text-secondary">No worker categories found</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-3">Name</th><th className="text-left py-3">Skill Level</th><th className="text-left py-3">Status</th><th className="text-right py-3">Actions</th></tr></thead>
              <tbody>{categories.map((c: any) => (
                <tr key={c.id} className="border-b hover:bg-hover transition-colors">
                  <td className="py-3 font-medium">{c.name}</td>
                  <td className="py-3">{(c.skill_level || 'UNCLASSIFIED').replace('_', ' ')}</td>
                  <td className="py-3"><Badge variant={c.is_active ? 'approved' : 'inactive'}>{c.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td className="py-3 text-right space-x-1">
                    {c.is_active ? (
                      <Button variant="ghost" size="sm" className="text-warning hover:text-warning" disabled={toggling === c.id} onClick={() => setConfirmTarget({ item: c, action: 'deactivate' })}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-success hover:text-success" disabled={toggling === c.id} onClick={() => setConfirmTarget({ item: c, action: 'activate' })}>
                        Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setForm({ name: (c.name || '').replace(/-(Skilled|Semi Skilled|Unskilled)$/i, ''), skillLevel: c.skill_level || 'UNSKILLED' }); }} aria-label={`Edit ${c.name}`} title="Edit"><Pencil size={14} /></Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(c)} aria-label={`Permanently delete ${c.name}`} title="Permanently delete"><Trash2 size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'deactivate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Deactivate ${confirmTarget?.item?.name}?`}
        description="This record will remain in existing records and reports, but it will not be available for new selections."
        actionLabel="Deactivate"
        buttonStyle="amber"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Deactivating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />

      <ConfirmActionDialog
        open={!!confirmTarget && confirmTarget.action === 'activate'}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Activate ${confirmTarget?.item?.name}?`}
        description="This record will become available for new selections."
        actionLabel="Activate"
        buttonStyle="green"
        loading={!!confirmTarget && toggling === confirmTarget.item?.id}
        loadingLabel="Activating..."
        onConfirm={() => { const t = confirmTarget; setConfirmTarget(null); if (t) handleToggleStatus(t.item); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete Worker Category?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>{deleteTarget?.name}</strong> ({(deleteTarget?.skill_level || 'UNCLASSIFIED').replace('_', ' ')}). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete}>
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
